'use client';

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { ArrowUp, MousePointer2, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/shadcn/button';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { useAssistantLauncherSlots } from '@/shared/assistant';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import type { AssistantMoment } from '@/types/assistant';
import { useAssistantStore } from '../model/assistant.store';
import { useAssistantScreen } from '../hooks/use-assistant-screen';
import { useAssistantMoment } from '../hooks/use-assistant-moment';
import { applyPatchToForm } from '../lib/settings-patch';
import { AssistantBlocks } from './AssistantBlocks';
import { AssistantPanel, ASSISTANT_PANEL_EXIT_MS } from './AssistantPanel';

// Layout effect: the panel must stay mounted on the very frame it is closed,
// or the launcher would flash for one paint before the fold-away starts.
const useCloseLingerEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * The AI architect on every signed-in screen: its button, a proactive
 * moment's bubble beside it, and the panel. `aboveBottomNav` lifts
 * the button over the phone's floating tab bar when that bar is shown.
 */
export function AssistantRoot({ aboveBottomNav }: { aboveBottomNav: boolean }) {
  const screen = useAssistantScreen();
  // Below lg the panel is a floating island at the bottom, so the launcher is
  // the corner circle (beside Generate on the design screen); from lg up the
  // panel docks at the side and the launcher lives in the header.
  const { isLg } = useBreakpoint();
  const slots = useAssistantLauncherSlots((state) => state.slots);
  const isOpen = useAssistantStore((s) => s.isOpen);
  const open = useAssistantStore((s) => s.open);
  const { moment, dismiss, engage } = useAssistantMoment(screen.screen, screen.buildingId);

  usePendingPatch();

  const [lingering, setLingering] = useState(false);
  const wasOpen = useRef(false);
  useCloseLingerEffect(() => {
    if (isOpen) {
      wasOpen.current = true;
      setLingering(false);
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    setLingering(true);
    const timer = window.setTimeout(() => setLingering(false), ASSISTANT_PANEL_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  // A reload keeps the panel as it was.
  useEffect(() => {
    useAssistantStore.getState().restoreOpen();
  }, []);

  // Escape closes the panel, like the other side panels.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) useAssistantStore.getState().close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Closing folds the panel into the launcher's corner: it stays mounted for
  // the length of that animation before the launcher takes over.
  if (isOpen || lingering) return <AssistantPanel screen={screen} closing={!isOpen} />;

  // A moment is a bubble beside the button, or only a dot on it (the server
  // decides). Either way the button opens the conversation on the moment.
  const onLaunch = () => (moment ? void engage() : open());
  const badge = moment?.attributes.presentation === 'badge';
  const bubble = moment && !badge && (
    <MomentBubble moment={moment} onDismiss={dismiss} onEngage={engage} />
  );

  // The screen decides where the button lives (AssistantLauncherSlot): the
  // header toolbar from lg up, the Generate row below it on the design screen.
  // Elsewhere it is a compact circle in the corner.
  const slot = isLg ? slots.toolbar : slots.footer;
  if (slot) {
    return createPortal(
      <Launcher
        variant={isLg ? 'toolbar' : 'circle'}
        hero={slot.dataset.tone === 'hero'}
        bubble={bubble}
        badge={badge}
        onLaunch={onLaunch}
      />,
      slot
    );
  }

  return (
    <div
      className={cn(
        'fixed z-40 end-4 pointer-events-none lg:end-6 lg:bottom-6',
        aboveBottomNav ? 'bottom-[calc(76px+env(safe-area-inset-bottom))]' : 'bottom-[calc(16px+env(safe-area-inset-bottom))]'
      )}
    >
      <Launcher variant="circle" bubble={bubble} badge={badge} onLaunch={onLaunch} />
    </div>
  );
}

/**
 * The button itself, the same pointer everywhere. `toolbar`: a header action
 * like Feedback, the pointer and the label. `circle`: a 40 circle with the
 * pointer, as tall as the Generate capsule it sits beside. A moment's bubble hangs off
 * the button: below the toolbar button, above the circle. A moment offered
 * quietly is a `badge`: a dot on the button's top end corner.
 */
function Launcher({
  variant,
  hero = false,
  bubble,
  badge,
  onLaunch,
}: {
  variant: 'toolbar' | 'circle';
  hero?: boolean;
  bubble: ReactNode;
  badge: boolean;
  onLaunch: () => void;
}) {
  const t = useTranslations('assistant');
  return (
    <div className="relative pointer-events-auto">
      {bubble && (
        <div
          className={cn(
            'absolute end-0 z-50',
            variant === 'toolbar' ? 'top-full mt-2' : 'bottom-full mb-2'
          )}
        >
          {bubble}
        </div>
      )}
      {variant === 'toolbar' ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onLaunch}
          className={cn(
            'h-9 text-toolbar-action',
            // Same glass pill as the Feedback button beside it.
            hero &&
              'bg-background/60 backdrop-blur-sm border-transparent text-foreground hover:bg-background/80'
          )}
        >
          {/* The same pointer as the circle, in the action's own colour like Feedback's icon. */}
          <MousePointer2 className="size-4 fill-current" aria-hidden />
          {t('launcher')}
        </Button>
      ) : (
        <button
          type="button"
          onClick={onLaunch}
          aria-label={t('launcher')}
          title={t('launcher')}
          // The bottom navigation's glass and unselected tint, so the circle reads
          // as part of the navigation, not as an action.
          className="flex size-10 items-center justify-center rounded-full border border-border/40 bg-background/40 backdrop-blur-2xl backdrop-saturate-150 text-form-label font-semibold text-muted-foreground shadow-[0_8px_32px_-8px_rgba(0,0,0,0.18)] transition-transform hover:scale-[1.04] active:scale-[0.96]"
        >
          {/* A pointer: the assistant acts in the app, not only answers. */}
          <MousePointer2 className="size-[18px] fill-current" aria-hidden />
        </button>
      )}
      {badge && (
        <span
          aria-hidden
          className="pointer-events-none absolute -top-0.5 -end-0.5 size-2.5 rounded-full bg-primary ring-2 ring-background"
        />
      )}
    </div>
  );
}

/**
 * The moment beside the button: its blocks (an option is sent as the reply),
 * a one-line field to answer in one's own words, and a close button. Sending
 * opens the conversation with that text, exactly like an option.
 */
export function MomentBubble({
  moment,
  onDismiss,
  onEngage,
}: {
  moment: AssistantMoment;
  onDismiss: () => void;
  onEngage: (reply?: string) => void;
}) {
  const t = useTranslations('assistant');
  const [reply, setReply] = useState('');
  const send = () => {
    if (reply.trim()) onEngage(reply.trim());
  };
  return (
    <div
      role="status"
      className="pointer-events-auto relative w-[280px] max-w-[calc(100vw-32px)] space-y-3 rounded-[16px] border border-border/40 bg-background p-3 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.18)]"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('close')}
        className="absolute end-1.5 top-1.5 flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
      >
        <X className="size-4" />
      </button>
      <div className="pe-7">
        <AssistantBlocks
          messageId={`moment:${moment.id}`}
          blocks={moment.attributes.blocks}
          optionsEnabled
          onOption={(label) => onEngage(label)}
          onAction={() => onEngage()}
        />
      </div>
      <div className="flex items-center gap-1 rounded-full border border-border/60 bg-field ps-3 pe-1 py-1">
        <input
          type="text"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={t('placeholder')}
          aria-label={t('placeholder')}
          className="flex-1 min-w-0 bg-transparent py-1.5 text-composer-input text-foreground outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={send}
          disabled={!reply.trim()}
          aria-label={t('send')}
          className="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:bg-button-disabled disabled:text-white"
        >
          <ArrowUp className="size-4" strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
}

/**
 * A settings patch that arrived while its design was still opening (the
 * server sent `open_design` first) lands as soon as that design's form loads.
 */
function usePendingPatch() {
  const pending = useAssistantStore((s) => s.pendingPatch);
  const loadedDesignId = useRenderStore((s) => s.currentContext?.conceptId);
  useEffect(() => {
    if (pending && loadedDesignId === pending.designId && applyPatchToForm(pending)) {
      useAssistantStore.getState().setPendingPatch(null);
    }
  }, [pending, loadedDesignId]);
}
